import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { itemsDetail, statusItems } from '../util/event-log-items.js';
import { Address, Status } from '../util/schemas.js';
import { assertCan, EventLogs } from '../utils.js';
import { UpdateResourceStatusSchema, addEventLog } from './util.js';
import { createAddress, updateAddress } from './utils/address-utils.js';
export const CustomerSchema = z.object({
    name: z.string(),
    pharmacyId: z.uuidv7(),
    email: z.string().optional(),
    phone: z.string().optional(),
    address: Address,
    deliveryAddress: Address.optional(),
    isSameAddress: z.boolean().optional(),
    status: Status.optional(),
    ikNumber: z.string().trim().nonempty(),
    producerNumber: z.string().optional(),
    labelName: z.string().optional(),
});
export const AddCustomerSchema = CustomerSchema;
export const UpdateCustomerSchema = AddCustomerSchema.partial().extend({ id: z.uuidv7() });
export const customers = {
    add: defineMutator(AddCustomerSchema, async ({ args: customer, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        if (customer.ikNumber) {
            const existing = await tx.run(zql.customers.where('ikNumber', '=', customer.ikNumber).one());
            if (existing)
                throw new Error('Customer IK number already exists');
        }
        const address = await createAddress(tx, customer.address, ctx.user.id);
        let deliveryAddressId = address.id;
        if (customer.deliveryAddress && !customer.isSameAddress) {
            const deliveryAddress = await createAddress(tx, customer.deliveryAddress, ctx.user.id);
            deliveryAddressId = deliveryAddress.id;
        }
        const now = Date.now();
        const newCustomer = {
            id: uuidv7(),
            name: customer.name,
            pharmacyId: customer.pharmacyId,
            isSameAddress: customer.isSameAddress ?? true,
            addressId: address.id,
            deliveryAddressId,
            ikNumber: customer.ikNumber,
            producerNumber: customer.producerNumber,
            labelName: customer.labelName,
            email: customer.email,
            phone: customer.phone,
            status: customer.status ?? 'active',
            createdAt: now,
            createdBy: ctx.user.id,
        };
        await tx.mutate.customers.insert(newCustomer);
        if (tx.location === 'server') {
            const patches = createPatch({}, newCustomer);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'customer',
                resourceId: newCustomer.id,
                action: 'create',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: newCustomer.id,
                    name: newCustomer.name,
                },
            }, ctx);
        }
    }),
    update: defineMutator(UpdateCustomerSchema, async ({ args: customer, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const prev = await tx.run(zql.customers.where('id', '=', customer.id).one());
        if (!prev)
            throw new Error('Not Found');
        if (customer.ikNumber) {
            const existing = await tx.run(zql.customers.where('ikNumber', '=', customer.ikNumber).where('id', '!=', customer.id).one());
            if (existing)
                throw new Error('Customer IK number already exists');
        }
        if (prev.addressId && customer.address) {
            await updateAddress(tx, prev.addressId, customer.address, ctx.user.id);
        }
        let deliveryAddressId = prev.deliveryAddressId;
        if (prev.isSameAddress !== customer.isSameAddress) {
            if (customer.isSameAddress) {
                deliveryAddressId = prev.addressId;
            }
            else if (customer.deliveryAddress) {
                const newDeliveryAddress = await createAddress(tx, customer.deliveryAddress, ctx.user.id);
                deliveryAddressId = newDeliveryAddress.id;
            }
        }
        delete customer.address;
        delete customer.deliveryAddress;
        const now = Date.now();
        const updatedCustomer = {
            ...customer,
            id: prev.id,
            deliveryAddressId,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.customers.update(updatedCustomer);
        if (tx.location === 'server') {
            const patches = createPatch(prev, { ...prev, ...updatedCustomer });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'customer',
                resourceId: prev.id,
                action: 'update',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: updatedCustomer.id,
                    name: updatedCustomer.name,
                },
            }, ctx);
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const prev = await tx.run(zql.customers.where('id', '=', args.id).one());
        if (!prev)
            throw new Error('Not found');
        const updatedCustomer = {
            id: args.id,
            status: args.status,
            updatedAt: Date.now(),
            updatedBy: ctx.user.id,
        };
        await tx.mutate.customers.update(updatedCustomer);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedCustomer);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'customer',
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedCustomer.updatedBy,
                performedAt: updatedCustomer.updatedAt,
                status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                domain: 'configuration',
                details: {
                    id: updatedCustomer.id,
                    name: prev.name,
                    ...itemsDetail(statusItems(prev.status, args.status)),
                },
            }, ctx);
        }
    }),
    deactivateBatch: defineMutator(z.object({ customerIds: z.array(z.uuidv7()) }), async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const customers = await tx.run(zql.customers.where('id', 'IN', args.customerIds).where('status', '=', 'active'));
        const now = Date.now();
        await Promise.all(customers.map(async (c) => await tx.mutate.customers.update({
            id: c.id,
            status: 'inactive',
            updatedAt: now,
            updatedBy: ctx.user.id,
        })));
        if (tx.location === 'server') {
            await Promise.all(customers.map(async (c) => {
                const patches = createPatch(c, { ...c, status: 'inactive', updatedAt: now, updatedBy: ctx.user.id });
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'customer',
                    resourceId: c.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: 'WARNING',
                    domain: 'configuration',
                    details: {
                        name: c.name,
                        id: c.id,
                    },
                    event: EventLogs.CUSTOMERS_DEACTIVATED,
                }, ctx);
            }));
        }
    }),
    deleteBatch: defineMutator(z.object({ customerIds: z.array(z.uuidv7()) }), async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const customers = await tx.run(zql.customers.where('id', 'IN', args.customerIds));
        const now = Date.now();
        await Promise.all(customers.map(async (c) => await tx.mutate.customers.update({
            id: c.id,
            deletedAt: now,
            deletedBy: ctx.user.id,
            ikNumber: `${c.ikNumber}_deleted_${now}`,
        })));
        if (tx.location === 'server') {
            await Promise.all(customers.map(async (c) => {
                const patches = createPatch(c, {
                    ...c,
                    deletedAt: now,
                    deletedBy: ctx.user.id,
                    ikNumber: `${c.ikNumber}_deleted_${now}`,
                });
                await addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'customer',
                    resourceId: c.id,
                    action: 'delete',
                    patches,
                    performedBy: ctx.user.id,
                    status: 'WARNING',
                    domain: 'configuration',
                    details: {
                        name: c.name,
                        id: c.id,
                    },
                    performedAt: now,
                    event: EventLogs.CUSTOMERS_DELETED,
                }, ctx);
            }));
        }
    }),
};
//# sourceMappingURL=customer.mutators.js.map